import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useImperativeHandle, useState } from 'react';
import { Card, Checkbox, Col, Form, Input, Row, Select, Spin, Switch } from 'antd';
import { axiosErrorHandler, showErrorNotification } from '../../../../../swzutils';
import AddressPanel from '../../../components/AddressPanel';
import HelpLabel from "../../../components/HelpLabel";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import SenderReceiverResource from "../api/SenderReceiverResource";
import RequireTenantSelection from "../../../components/RequireTenantSelection";
import DivisionAccessFormItem from "../../divisions/DivisionAccessFormItem";
import DayOfWeekType from "../../../enums/DayOfWeekType";
import WaypointUhfChannelsCardFormItems from "../../consignments/waypoint/WaypointUhfChannelsCardFormItems";
import { useAppSelector } from "../../../../../store";
import { selectedTenantSelector } from "../../../../../store/slice/auth";
import LocalTimePicker from "../../../components/LocalTimePicker";
const SenderReceiverEditForm = ({ senderReceiverId }, exportedRef) => {
    const [networkRequestInProgress, setNetworkRequestInProgress] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [addressLatLng, setAddressLatLng] = useState(undefined);
    const [addressSiteRadius, setAddressSiteRadius] = useState(200);
    const [editSenderReceiver, setEditSenderReceiver] = useState(undefined);
    const [form] = Form.useForm();
    const selectedTenant = useAppSelector(selectedTenantSelector);
    const openDaysWatched = Form.useWatch('openDays', form) ?? [];
    useImperativeHandle(exportedRef, () => ({
        save: () => {
            return new Promise((resolve, reject) => {
                validateAndSave(resolve, reject);
            });
        }
    }));
    useEffect(() => {
        if (senderReceiverId) {
            loadSenderReceiver(senderReceiverId);
        }
    }, []);
    const validateAndSave = (resolve, reject) => {
        if (addressLatLng && addressSiteRadius) {
            form.validateFields()
                .then(formValues => save(formValues, resolve, reject))
                .catch(reject);
        }
        else {
            showErrorNotification("Please position the map crosshairs at the location for this address");
            reject();
        }
    };
    const save = (formValues, resolve, reject) => {
        let request = {
            code: formValues.code,
            tenantId: editSenderReceiver?.tenant.id || selectedTenant?.id,
            uhfChannelOffice: formValues.uhfChannelOffice,
            uhfChannelLoader: formValues.uhfChannelLoader,
            pickupDockets: formValues.pickupDockets,
            allowsPickups: formValues.allowsPickups,
            companyName: formValues.companyName,
            contactName: formValues.contactName,
            contactTelephone: formValues.contactTelephone,
            contactEmail: formValues.contactEmail,
            openDays: formValues.openDays,
            openingHours: buildOpeningHoursRequest(formValues.openDays, formValues.openingHours),
            version: editSenderReceiver?.version || null,
            divisionId: formValues.divisionId,
            disable: disabled,
            address: {
                unitOrFlat: formValues.unitOrFlat,
                street: formValues.street,
                suburb: formValues.suburb,
                state: formValues.state,
                postcode: formValues.postcode,
                countryCode: formValues.countryCode,
                latitude: addressLatLng?.lat,
                longitude: addressLatLng?.lng,
                siteRadius: addressSiteRadius,
            }
        };
        setNetworkRequestInProgress(true);
        if (senderReceiverId) {
            SenderReceiverResource.update(senderReceiverId, request, resolve, axiosError => {
                axiosErrorHandler(axiosError);
                reject(axiosError);
            }, () => setNetworkRequestInProgress(false));
        }
        else {
            SenderReceiverResource.create(request, resolve, axiosError => {
                axiosErrorHandler(axiosError);
                reject(axiosError);
            }, () => setNetworkRequestInProgress(false));
        }
    };
    const buildOpeningHoursRequest = (openDays, openingHours) => {
        if (!openDays || !openingHours) {
            return [];
        }
        return openDays
            .map(dayOfWeek => ({ dayOfWeek, ...openingHours[dayOfWeek] }))
            .filter((it) => !!it.openingTime && !!it.closingTime);
    };
    const buildOpeningHoursFieldValue = (openingHours) => {
        const fieldValue = {};
        (openingHours ?? []).forEach(it => {
            fieldValue[it.dayOfWeek] = { openingTime: it.openingTime, closingTime: it.closingTime };
        });
        return fieldValue;
    };
    const loadSenderReceiver = (senderReceiverId) => {
        setNetworkRequestInProgress(true);
        SenderReceiverResource.get(senderReceiverId, onSenderReceiverLoaded, axiosErrorHandler, () => setNetworkRequestInProgress(false));
    };
    const onSenderReceiverLoaded = (senderReceiver) => {
        setEditSenderReceiver(senderReceiver);
        setDisabled(!!senderReceiver.disabledAt);
        if (senderReceiver.address) {
            setAddressSiteRadius(senderReceiver.address.siteRadius || 200);
            if (senderReceiver.address.latitude && senderReceiver.address.longitude) {
                setAddressLatLng({
                    lat: senderReceiver.address.latitude,
                    lng: senderReceiver.address.longitude
                });
            }
        }
        form.setFieldsValue({
            code: senderReceiver.code,
            uhfChannelOffice: senderReceiver.uhfChannelOffice,
            uhfChannelLoader: senderReceiver.uhfChannelLoader,
            pickupDockets: senderReceiver.pickupDockets ? "true" : "false",
            allowsPickups: senderReceiver.allowsPickups ? "true" : "false",
            divisionId: senderReceiver.division?.id,
            companyName: senderReceiver.companyName,
            contactName: senderReceiver.contactName,
            contactTelephone: senderReceiver.contactTelephone,
            contactEmail: senderReceiver.contactEmail,
            openDays: senderReceiver.openDays ?? DayOfWeekType.values.map(it => it.key),
            openingHours: buildOpeningHoursFieldValue(senderReceiver.openingHours),
            unitOrFlat: senderReceiver.address?.unitOrFlat,
            street: senderReceiver.address?.street,
            suburb: senderReceiver.address?.suburb,
            state: senderReceiver.address?.state,
            postcode: senderReceiver.address?.postcode,
            countryCode: senderReceiver.address?.countryCode,
            enabled: !senderReceiver.disabledAt,
        });
    };
    const onAddressLocationChanged = (latlng) => {
        setAddressLatLng(latlng);
    };
    const onSiteRadiusChanged = (siteRadius) => {
        setAddressSiteRadius(siteRadius);
    };
    if (!senderReceiverId && !selectedTenant?.id) {
        return (_jsx(RequireTenantSelection, { children: _jsx("p", { children: "&nbsp" }) }));
    }
    return (_jsx(Spin, { spinning: networkRequestInProgress, children: _jsxs(Form, { layout: "vertical", form: form, children: [_jsxs(Row, { children: [_jsx(Col, { lg: 8, sm: 12, xs: 24, children: _jsxs(Card, { title: "Active and Import", type: "inner", variant: "borderless", children: [_jsx(Form.Item, { name: "enabled", label: "Active", children: _jsx(Switch, { checkedChildren: _jsx(CheckOutlined, {}), unCheckedChildren: _jsx(CloseOutlined, {}), checked: !disabled, onChange: (checked) => setDisabled(!checked) }) }), _jsx(Form.Item, { name: "code", label: (_jsx(HelpLabel, { title: "Import Code", helpText: "Optional code that is used to identify a saved location for use with csv import of deliveries hence must be unique.  If in doubt just use the company or contact name with hyphens or underscores for spaces.  e.g. XYZ-CITY-COUNCIL" })), rules: [
                                            { pattern: /^[0-9a-z\-_]+$/i, message: 'The code should only contain letters, numbers (-) hyphen and (_) underscore' },
                                            { max: 30, message: 'Please limit the code to 30 characters.' }
                                        ], children: _jsx(Input, { disabled: disabled }) }), _jsx(Form.Item, { name: "pickupDockets", label: (_jsx(HelpLabel, { title: "Pickup Dockets?", helpText: "Determines if the driver will be prompted to take a pickup photo when this location is used for delivery pickup" })), children: _jsx(Select, { allowClear: true, defaultActiveFirstOption: false, options: [
                                                { value: "true", label: 'Yes (Location provides pickup dockets)' },
                                                { value: "false", label: 'No (Location doesn\'t supply pickup dockets)' }
                                            ] }) }), _jsx(Form.Item, { name: "allowsPickups", label: "Allow Pickups?", children: _jsx(Select, { allowClear: true, defaultActiveFirstOption: false, options: [
                                                { value: "true", label: 'Yes (Customers can pickup from this location)' },
                                                { value: "false", label: 'No (No pickups from this location)' }
                                            ] }) }), _jsx(DivisionAccessFormItem, { tenantId: editSenderReceiver?.tenant.id, name: "divisionId", allowMultiple: false, disabled: disabled })] }) }), _jsx(Col, { lg: 8, sm: 12, xs: 24, children: _jsx(WaypointUhfChannelsCardFormItems, {}) }), _jsx(Col, { lg: 8, sm: 12, xs: 24, children: _jsx(Card, { title: (_jsx(HelpLabel, { title: "Open Days", helpText: "What days is this location open for pickups or deliveries, and optionally its opening and closing time on each of those days." })), type: "inner", variant: "borderless", children: _jsx(Form.Item, { name: "openDays", initialValue: DayOfWeekType.BUSINESS_DAYS.map(it => it.key), rules: [
                                        { required: !disabled, message: 'Select at least one open day.  If this location is no longer open turn the "Active" switch off.' }
                                    ], children: _jsx(Checkbox.Group, { style: { width: '100%' }, disabled: disabled, children: _jsx(Row, { children: DayOfWeekType.values.map(day => (_jsx(Col, { span: 24, children: _jsxs(Row, { align: "middle", gutter: 8, style: { marginBottom: 4 }, children: [_jsx(Col, { flex: "130px", children: _jsx(Checkbox, { value: day.key, children: day.description }) }), _jsx(Col, { flex: "auto", children: _jsx(Form.Item, { name: ['openingHours', day.key, 'openingTime'], noStyle: true, children: _jsx(LocalTimePicker, { placeholder: "Opens", allowClear: true, disabled: disabled || !openDaysWatched.includes(day.key) }) }) }), _jsx(Col, { flex: "auto", children: _jsx(Form.Item, { name: ['openingHours', day.key, 'closingTime'], noStyle: true, children: _jsx(LocalTimePicker, { placeholder: "Closes", allowClear: true, disabled: disabled || !openDaysWatched.includes(day.key) }) }) })] }) }, day.key))) }) }) }) }) })] }), _jsx(AddressPanel, { form: form, waypoint: editSenderReceiver, showCompanyContactDetails: true, showMapAlways: true, showStatus: true, onLocationChanged: onAddressLocationChanged, onSiteRadiusChanged: onSiteRadiusChanged, readOnly: disabled })] }) }));
};
export default React.forwardRef(SenderReceiverEditForm);
